// Client-side PDF summary generator for a Studio color scheme.
// Uses pdf-lib (no server round-trip, no additional install).

import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import type { SharePaletteItem } from "@/lib/studio/shares";

export type PdfInput = {
  projectName: string;
  sourceImageUrl: string;
  renderedImageUrl: string | null;
  palette: SharePaletteItem[];
  shareUrl?: string;
};

async function fetchImage(url: string): Promise<{ bytes: Uint8Array; mime: string } | null> {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const buf = new Uint8Array(await res.arrayBuffer());
    const mime = res.headers.get("content-type") || (url.endsWith(".png") ? "image/png" : "image/jpeg");
    return { bytes: buf, mime };
  } catch { return null; }
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export async function generateSummaryPdf(input: PdfInput): Promise<Blob> {
  const doc = await PDFDocument.create();
  const page = doc.addPage([612, 792]); // US Letter
  const width = 612;
  const height = 792;
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);

  const margin = 40;
  let y = height - margin;

  page.drawText("Michael's Painting — Color Scheme", { x: margin, y, size: 18, font: bold, color: rgb(0.1, 0.1, 0.1) });
  y -= 22;
  page.drawText(input.projectName || "Untitled project", { x: margin, y, size: 12, font, color: rgb(0.35, 0.35, 0.35) });
  y -= 12;
  page.drawText(new Date().toLocaleString(), { x: margin, y, size: 9, font, color: rgb(0.55, 0.55, 0.55) });
  y -= 20;

  // Image (render or source)
  const imgUrl = input.renderedImageUrl || input.sourceImageUrl;
  const img = await fetchImage(imgUrl);
  if (img) {
    try {
      const embedded = img.mime.includes("png") ? await doc.embedPng(img.bytes) : await doc.embedJpg(img.bytes);
      const maxW = width - margin * 2;
      const maxH = 320;
      const ratio = Math.min(maxW / embedded.width, maxH / embedded.height);
      const w = embedded.width * ratio;
      const h = embedded.height * ratio;
      page.drawImage(embedded, { x: margin, y: y - h, width: w, height: h });
      y -= h + 20;
    } catch { /* skip on decode error */ }
  }

  // Palette heading
  page.drawText("Selected Colors", { x: margin, y, size: 13, font: bold, color: rgb(0.1, 0.1, 0.1) });
  y -= 16;

  for (const p of input.palette) {
    if (y < margin + 40) break; // stay on one page for simplicity
    const [r, g, b] = hexToRgb(p.hex);
    page.drawRectangle({ x: margin, y: y - 20, width: 28, height: 20, color: rgb(r, g, b), borderColor: rgb(0.85, 0.85, 0.85), borderWidth: 0.5 });
    page.drawText(`${p.name}  ·  ${p.number}`, { x: margin + 40, y: y - 8, size: 10, font: bold, color: rgb(0.15, 0.15, 0.15) });
    page.drawText(`${p.brand}  ·  ${p.surface}  ·  ${p.hex.toUpperCase()}`, { x: margin + 40, y: y - 20, size: 8, font, color: rgb(0.45, 0.45, 0.45) });
    y -= 30;
  }

  // Footer
  const footerY = margin;
  page.drawText("Michael's Painting  ·  michaelspaintingwi.com", { x: margin, y: footerY + 12, size: 9, font, color: rgb(0.4, 0.4, 0.4) });
  if (input.shareUrl) {
    page.drawText(input.shareUrl, { x: margin, y: footerY, size: 8, font, color: rgb(0.55, 0.55, 0.55) });
  }

  const bytes = await doc.save();
  return new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}